import { Injectable, useService } from '@visiba/instantiation';
import { DialogAlert } from '@cellula/react';
import { type AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { signal } from '@preact/signals-react';

@Injectable()
export class ScenarioService {
	public escalatedScenario = signal<Nullable<JSX.Element>>(null);

	constructor(private readonly analyticService: AnalyticService) {}

	public async escalateScenarios<T>(scenarios: Scenario<T>[], data: T): Promise<boolean> {
		let escalation: Scenario<T> | null = null;
		for (const scenario of scenarios) {
			if (scenario.escalation(data)) {
				escalation = scenario;

				break;
			}
		}

		if (escalation != null) {
			const def = new DeferredPromise<boolean>();

			if (escalation.analyticQuirk_category && escalation.analyticQuirk_name) {
				this.analyticService.trackEvent(
					escalation.analyticQuirk_category,
					`escalates scenario ${escalation.analyticQuirk_name}`,
					'escalate',
				);
			}

			this.escalatedScenario.value = (
				<DialogAlert
					onAbort={() => {
						def.reject(false);
						this.escalatedScenario.value = null;
					}}
					onSuccess={() => {
						def.resolve(true);
						this.escalatedScenario.value = null;
					}}
					header={escalation.header}
					description={escalation.description}
					successText={escalation.continue}
					successButtonVariant='primary'
					abortText={escalation.abort}
					isOpen
				/>
			);

			// Yes.
			// It's cursed.
			// But quite effective.
			try {
				// Waits for the user to preform an action on the modal.
				await def.promise;
			} catch {
				// If the user "aborts" then we exit the method.
				return false;
			}
		}

		return true;
	}
}

class DeferredPromise<T> {
	public resolve: (value: T) => void = () => {};
	public reject: (value: T) => void = () => {};
	public readonly promise: Promise<T>;

	constructor() {
		this.promise = new Promise<T>((resolve, reject) => {
			this.reject = reject;
			this.resolve = resolve;
		});
	}
}

export interface Scenario<T> {
	readonly analyticQuirk_name?: string;
	readonly analyticQuirk_category?: AnalyticCategories;
	readonly header: string;
	readonly description: string;
	readonly continue: string;
	readonly abort: string;
	escalation(collectedData: T): boolean;
}

function ScenarioRoot(props: React.PropsWithChildren) {
	const scenarioService = useService(ScenarioService);

	return (
		<>
			{scenarioService.escalatedScenario.value}
			{props.children}
		</>
	);
}

export const Scenario = {
	Root: ScenarioRoot,
} as const;
